import { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { 
  ArrowLeft, 
  Calendar, 
  CheckCircle, 
  Clock, 
  Dumbbell,
  Save,
  SkipForward,
  AlertTriangle,
  Info,
  ChevronLeft,
  ChevronRight
} from 'lucide-react'
import { 
  format, 
  startOfWeek, 
  endOfWeek, 
  eachDayOfInterval, 
  isSameDay,
  addDays,
  startOfMonth,
  endOfMonth,
  addMonths,
  subMonths,
  isSameMonth
} from 'date-fns'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { useProgramStore } from '@/stores/programStore'
import { ProgramService } from '@/services'
import toast from 'react-hot-toast'

interface GeneratedWorkout {
  id: string
  name: string
  type: 'strength' | 'cardio' | 'flexibility' | 'mixed'
  estimatedDuration: number
  difficulty: number
  exercises: Array<{
    name: string
    sets?: number
    reps?: string
    restTime?: number
  }>
  week: number
  originalDay?: number
}

interface WorkoutAssignment {
  workoutId: string
  assignedDate: Date
}

interface ProgramDayAssignmentViewProps {
  onComplete: (assignments: WorkoutAssignment[]) => void
  onSkip: () => void
}

export function ProgramDayAssignmentView({ onComplete, onSkip }: ProgramDayAssignmentViewProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const {} = useProgramStore() // Keep import for future use
  
  const [generatedWorkouts, setGeneratedWorkouts] = useState<GeneratedWorkout[]>([])
  const [assignments, setAssignments] = useState<WorkoutAssignment[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [selectedWorkout, setSelectedWorkout] = useState<GeneratedWorkout | null>(null)
  const [draggedWorkout, setDraggedWorkout] = useState<GeneratedWorkout | null>(null)
  const [completed, setCompleted] = useState(false)
  // Get program details from URL params or store
  const programId = searchParams.get('programId')
  
  // Extract search params once to avoid recreating on every render
  const startDateParam = searchParams.get('startDate')
  const endDateParam = searchParams.get('endDate')
  const lengthParam = searchParams.get('programLength')
  
  // Create a stable default date outside useMemo
  const defaultStartRef = useRef<Date>()
  if (!defaultStartRef.current) {
    defaultStartRef.current = new Date()
    defaultStartRef.current.setHours(0, 0, 0, 0) // Normalize to start of day
  }
  
  // Memoize date calculations to prevent infinite re-renders
  const { startDate, endDate } = useMemo(() => {
    const start = startDateParam ? new Date(startDateParam) : defaultStartRef.current!
    const end = endDateParam ? new Date(endDateParam) : addDays(start, 56) // 8 weeks default
    const length = parseInt(lengthParam || '8')
    
    return { startDate: start, endDate: end, programLength: length }
  }, [startDateParam, endDateParam, lengthParam])
  
  // Initialize currentMonth state with a default date
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date())
  const isInitialized = useRef(false)
  
  // Get navigation state
  const navigationState = location.state as { generatedProgram?: any }

  // Calculate calendar range for current month view
  const monthStart = startOfMonth(currentMonth)
  const monthEnd = endOfMonth(currentMonth)
  const calendarStart = startOfWeek(monthStart)
  const calendarEnd = endOfWeek(monthEnd)
  const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd })

  // Initialize current month to start date only once
  useEffect(() => {
    if (!isInitialized.current && startDate) {
      setCurrentMonth(startDate)
      isInitialized.current = true
    }
  }, [startDate])

  useEffect(() => {
    // Don't reload if we've already completed the assignment
    if (!completed) {
      console.log('🔄 useEffect triggered, loading workouts...', { programId, completed })
      loadGeneratedWorkouts()
    } else {
      console.log('⏭️ Skipping workout reload - assignment already completed')
    }
  }, [programId, completed])

  const loadGeneratedWorkouts = async () => {
    try {
      setLoading(true)
      
      // Check if we have program data from navigation state
      console.log('🔍 Navigation state check:', { 
        hasNavigationState: !!navigationState, 
        hasGeneratedProgram: !!navigationState?.generatedProgram,
        programId,
        navigationKeys: navigationState ? Object.keys(navigationState) : 'none'
      })
      
      if (navigationState?.generatedProgram) {
        console.log('Using program data from navigation state:', navigationState.generatedProgram)
        
        // Check if the program data includes actual workouts
        const programData = navigationState.generatedProgram
        
        // Try to fetch real workouts from the database
        console.log('🔍 Fetching real workouts from database for program:', programData.id)
        try {
          const realWorkouts = await ProgramService.getProgramWorkouts(programData.id)
          if (realWorkouts && realWorkouts.length > 0) {
            console.log('✅ Using real workouts from database:', realWorkouts.length)
            console.log('🔍 Sample workout data:', realWorkouts[0])
            
            // Convert database workouts to GeneratedWorkout format
            const convertedWorkouts: GeneratedWorkout[] = realWorkouts.map((workout: any) => ({
              id: workout.id,
              name: workout.name || `Week ${workout.week} - Day ${workout.day}`,
              type: 'strength', // Default type, could be enhanced
              estimatedDuration: workout.estimatedDuration || 45,
              difficulty: workout.difficulty || 5,
              exercises: workout.exercises?.map((ex: any) => ({
                name: ex.exercise?.name || 'Exercise',
                sets: ex.sets,
                reps: ex.reps,
                restTime: ex.restTime
              })) || [],
              week: workout.week,
              originalDay: workout.day
            }))
            
            console.log('🔧 Converted workouts with real IDs:', convertedWorkouts.map(w => ({ id: w.id, name: w.name })))
            setGeneratedWorkouts(convertedWorkouts)
            toast.success('✨ Real program workouts loaded successfully')
          } else {
            throw new Error('No workouts found in database')
          }
        } catch (error) {
          console.error('❌ Could not fetch real workouts:', error)
          // NO MOCK DATA - Show real error
          toast.error(`Failed to load program workouts: ${error instanceof Error ? error.message : 'Unknown error'}`)
          throw error
        }
        return
      }
      
      // No navigation state data, try to fetch from API
      if (!programId) {
        console.warn('❌ No program ID found in URL parameters')
        throw new Error('No program ID found in URL parameters')
      }
      
      if (programId === 'temp-program') {
        console.warn('❌ Temporary program ID detected, cannot fetch from API')
        throw new Error('Temporary program ID detected - program not yet saved to database')
      }

      // Fetch program details and generated workouts from API
      console.log('Fetching program by ID:', programId)
      const program = await ProgramService.getProgramById(programId)
      console.log('Program fetched successfully:', program)
      
      // Check if program has workouts, otherwise throw error
      if ((program as any).workouts && (program as any).workouts.length > 0) {
        setGeneratedWorkouts((program as any).workouts)
      } else {
        throw new Error('Program has no workouts. Please regenerate the program with proper workout data.')
      }
      
      toast.success('✨ Program workouts loaded successfully')
    } catch (error) {
      console.error('Failed to load generated workouts:', error)
      
      // Show error dialog instead of automatic fallback
      let errorMessage = 'Failed to load program workouts. This might be because:\n\n'
      
      const errorMsg = error instanceof Error ? error.message : String(error)
      
      if (errorMsg.includes('Temporary program ID')) {
        errorMessage += '• The program was just generated but not yet saved to the database\n' +
                      '• You may need to wait a moment for the save process to complete\n\n'
      } else if (errorMsg.includes('No program ID')) {
        errorMessage += '• The URL is missing the program ID parameter\n' +
                      '• Navigation state was lost during page transition\n\n'
      } else if (errorMsg.includes('Program not found')) {
        errorMessage += '• The program ID doesn\'t exist in the database\n' +
                      '• The program may have been deleted\n\n'
      } else {
        errorMessage += '• The fit-engine service is unavailable\n' +
                      '• There was a connection error\n' +
                      '• The backend service is down\n\n'
      }
      
      errorMessage += 'Would you like to continue with sample workouts or go back to fix the issue?\n\n' +
                     'Click OK for sample workouts, Cancel to go back to program creation.'
      
      const shouldUseFallback = window.confirm(errorMessage)
      
      if (shouldUseFallback) {
        console.log('User chose to handle error')
        toast.error('Unable to load program workouts. Please regenerate the program.')
        navigate('/programs')
        return
      } else {
        console.log('User chose to go back')
        toast.error('Returning to program creation')
        navigate('/programs/create')
        return
      }
    } finally {
      setLoading(false)
    }
  }

  /*
  const loadWorkoutsFromEnhancedProgram = async (programId: string): Promise<GeneratedWorkout[]> => {
    try {
      console.log('🔄 Loading workouts from enhanced program:', programId)
      
      // Call enhanced API to get program with complete structure
      const { enhancedApiService } = await import('@/services/enhancedApi')
      const enhancedProgram = await enhancedApiService.getEnhancedProgram(programId)
      
      if (!enhancedProgram || !enhancedProgram.mesocycles) {
        throw new Error('Enhanced program structure not found - program may not be generated with enhanced system')
      }
      
      // Extract workouts from hierarchical structure
      const workouts: GeneratedWorkout[] = []
      
      for (const mesocycle of enhancedProgram.mesocycles) {
        for (const phase of mesocycle.phases) {
          for (const week of phase.weeks) {
            Object.entries(week.schedule).forEach(([day, dayWorkouts]) => {
              dayWorkouts.forEach((workout: any) => {
                workouts.push({
                  id: workout.id,
                  name: workout.name,
                  type: 'strength', // Enhanced workouts are sophisticated
                  estimatedDuration: workout.estimatedDuration,
                  difficulty: 7, // Enhanced difficulty calculation
                  exercises: workout.assignedExercises.map((ex: any) => ex.exerciseName),
                  week: week.weekNumber,
                  originalDay: ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].indexOf(day)
                })
              })
            })
          }
        }
      }
      
      console.log(`✅ Loaded ${workouts.length} workouts from enhanced program structure`)
      return workouts
      
    } catch (error) {
      console.error('❌ Failed to load enhanced program workouts:', error)
      throw new Error(`Failed to load enhanced program workouts: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }
  */

  const getWorkoutsForDate = (date: Date) => {
    return assignments
      .filter(assignment => isSameDay(assignment.assignedDate, date))
      .map(assignment => generatedWorkouts.find(w => w.id === assignment.workoutId))
      .filter(Boolean) as GeneratedWorkout[]
  }

  const getUnassignedWorkouts = () => {
    const assignedIds = assignments.map(a => a.workoutId)
    return generatedWorkouts.filter(w => !assignedIds.includes(w.id))
  }

  const handleWorkoutDrop = (workout: GeneratedWorkout, date: Date) => {
    // Check if date is within program range
    if (date < startDate || date > endDate) {
      toast.error('Cannot assign workouts outside the program date range')
      return
    }

    // Check if date already has a workout and ask for confirmation
    const existingWorkouts = getWorkoutsForDate(date)
    if (existingWorkouts.length > 0) {
      const confirmReplace = window.confirm(
        `This date already has a workout (${existingWorkouts[0].name}). Do you want to replace it with ${workout.name}?`
      )
      if (!confirmReplace) {
        return
      }
      
      // Remove existing assignments for this date
      setAssignments(prev => prev.filter(a => !isSameDay(a.assignedDate, date)))
    }

    // Remove existing assignment for this workout (if any)
    setAssignments(prev => prev.filter(a => a.workoutId !== workout.id))
    
    // Add new assignment
    // Create UTC date to ensure consistency with backend storage
    const utcDate = new Date(Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      9, // 9 AM UTC as default workout time
      0,
      0,
      0
    ))
    setAssignments(prev => [...prev, {
      workoutId: workout.id,
      assignedDate: utcDate
    }])

    const action = existingWorkouts.length > 0 ? 'moved to' : 'assigned to'
    toast.success(`${workout.name} ${action} ${format(date, 'MMM d, yyyy')}`)
  }

  const handleWorkoutRemove = (workoutId: string) => {
    setAssignments(prev => prev.filter(a => a.workoutId !== workoutId))
    toast.success('Workout unassigned')
  }

  const handleAutoAssign = () => {
    const unassignedWorkouts = getUnassignedWorkouts()
    if (unassignedWorkouts.length === 0) {
      toast.success('All workouts are already assigned')
      return
    }

    // Try to get the original program schedule from navigation state or URL
    let preferredDays = [1, 3, 5] // Fallback: Monday, Wednesday, Friday
    
    console.log('🔍 Auto-assign: Looking for program schedule...')
    
    // Check if we have program data with schedule information
    const programData = navigationState?.generatedProgram
    console.log('📋 Program data from navigation:', programData)
    
    if (programData?.schedule) {
      preferredDays = programData.schedule
      console.log('✅ Using schedule from program data:', preferredDays)
    } else {
      // Try to parse from URL or form data if available
      const scheduleParam = searchParams.get('schedule')
      console.log('🔗 Schedule from URL params:', scheduleParam)
      
      if (scheduleParam) {
        try {
          preferredDays = JSON.parse(scheduleParam)
          console.log('✅ Using schedule from URL params:', preferredDays)
        } catch (error) {
          console.warn('❌ Failed to parse schedule from URL:', error)
          // Keep default if parsing fails
        }
      } else {
        console.warn('⚠️ No schedule found, using default:', preferredDays)
      }
    }

    // Sort workouts by week and type for better distribution
    const sortedWorkouts = [...unassignedWorkouts].sort((a, b) => {
      if (a.week !== b.week) return a.week - b.week
      // Prioritize strength training earlier in the week
      if (a.type === 'strength' && b.type !== 'strength') return -1
      if (b.type === 'strength' && a.type !== 'strength') return 1
      return 0
    })
    
    let currentDate = new Date(startDate)
    let workoutIndex = 0
    let newAssignments: WorkoutAssignment[] = []

    while (workoutIndex < sortedWorkouts.length && currentDate <= endDate) {
      const dayOfWeek = currentDate.getDay()
      
      // Check if this is a preferred workout day and no workout is already assigned
      if (preferredDays.includes(dayOfWeek)) {
        const existingWorkout = getWorkoutsForDate(currentDate)
        
        if (existingWorkout.length === 0) {
          const workout = sortedWorkouts[workoutIndex]
          // Create UTC date to ensure consistency with backend storage
          const utcDate = new Date(Date.UTC(
            currentDate.getFullYear(),
            currentDate.getMonth(),
            currentDate.getDate(),
            9, // 9 AM UTC as default workout time
            0,
            0,
            0
          ))
          newAssignments.push({
            workoutId: workout.id,
            assignedDate: utcDate
          })
          workoutIndex++
        }
      }
      
      // Move to next day
      currentDate = addDays(currentDate, 1)
    }

    // Apply all new assignments
    setAssignments(prev => [...prev, ...newAssignments])
    
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    const scheduleDays = preferredDays.map(day => dayNames[day]).join('/')
    toast.success(`Auto-assigned ${newAssignments.length} workouts to ${scheduleDays} schedule`)
  }

  const handleClearAll = () => {
    if (assignments.length === 0) return
    
    const confirmed = window.confirm(
      `Are you sure you want to clear all ${assignments.length} workout assignments? This action cannot be undone.`
    )
    
    if (confirmed) {
      setAssignments([])
      toast.success('All workout assignments cleared')
    }
  }

  const handleSaveAssignments = async () => {
    try {
      setSaving(true)
      
      // Validate that all workouts are assigned
      if (assignments.length !== generatedWorkouts.length) {
        const unassigned = generatedWorkouts.length - assignments.length
        const confirmed = window.confirm(
          `${unassigned} workouts are still unassigned. Save anyway?`
        )
        if (!confirmed) return
      }

      // Save assignments to the backend
      if (programId && programId !== 'temp-program') {
        console.log('💾 Saving assignments to backend:', { programId, assignments: assignments.length })
        console.log('🔍 Sample assignment data:', assignments.slice(0, 3).map(a => ({ workoutId: a.workoutId, date: a.assignedDate })))
        await ProgramService.saveWorkoutSchedule(programId, assignments)
        toast.success('Workout schedule saved successfully!')
      } else {
        // For temp programs or no program ID, just complete locally
        console.log('📝 No valid program ID, completing locally')
        toast.error('Schedule completed but not saved to database. Program will need to be regenerated.')
      }
      
      // Mark as completed to prevent reloading
      console.log('✅ Assignment completed successfully, setting completed flag')
      setCompleted(true)
      
      // Complete the assignment flow
      onComplete(assignments)
      
    } catch (error: any) {
      console.error('Failed to save assignments:', error)
      
      // Show detailed error message
      const errorMessage = error.message || 'Failed to save workout schedule'
      toast.error(`Save failed: ${errorMessage}`)
      
      // Ask user if they want to continue without saving
      const continueAnyway = window.confirm(
        `Failed to save workout schedule to the database:\n\n${errorMessage}\n\n` +
        'Would you like to continue anyway? (Changes will not be persisted)'
      )
      
      if (continueAnyway) {
        toast.error('Continuing without saving. Changes will be lost.')
        // Mark as completed to prevent reloading
        console.log('⚠️ Assignment completed without saving, setting completed flag')
        setCompleted(true)
        onComplete(assignments)
      }
    } finally {
      setSaving(false)
    }
  }

  const getDifficultyColor = (difficulty: number) => {
    if (difficulty <= 3) return 'text-green-600 bg-green-100'
    if (difficulty <= 6) return 'text-yellow-600 bg-yellow-100'
    return 'text-red-600 bg-red-100'
  }

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'strength': return <Dumbbell className="w-4 h-4" />
      case 'cardio': return <Clock className="w-4 h-4" />
      case 'flexibility': return <Calendar className="w-4 h-4" />
      default: return <Dumbbell className="w-4 h-4" />
    }
  }

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth(prev => 
      direction === 'prev' ? subMonths(prev, 1) : addMonths(prev, 1)
    )
  }

  const canNavigatePrev = () => {
    const prevMonth = subMonths(currentMonth, 1)
    const prevMonthEnd = endOfMonth(prevMonth)
    return prevMonthEnd >= startDate
  }

  const canNavigateNext = () => {
    const nextMonth = addMonths(currentMonth, 1)
    const nextMonthStart = startOfMonth(nextMonth)
    return nextMonthStart <= endDate
  }

  const getMonthlyAssignmentStats = () => {
    const currentMonthAssignments = assignments.filter(assignment => 
      isSameMonth(assignment.assignedDate, currentMonth)
    ).length
    
    const totalAssignments = assignments.length
    const otherMonthsAssignments = totalAssignments - currentMonthAssignments
    
    return {
      currentMonth: currentMonthAssignments,
      otherMonths: otherMonthsAssignments,
      total: totalAssignments
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center space-y-4">
          <LoadingSpinner size="lg" />
          <div>
            <h3 className="text-lg font-medium text-gray-900">Loading Generated Workouts</h3>
            <p className="text-gray-600">Preparing your program schedule...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/programs')}
            className="p-2 text-gray-400 hover:text-gray-600"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">Assign Workout Days</h1>
            </div>
            <p className="text-gray-600">
              Drag workouts to calendar days or skip to finalize later
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleAutoAssign}
            disabled={generatedWorkouts.length === 0 || assignments.length === generatedWorkouts.length}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            title={`Auto-assign ${getUnassignedWorkouts().length} remaining workouts to the program schedule`}
          >
            <Calendar className="w-4 h-4" />
            Auto-Assign ({getUnassignedWorkouts().length})
          </button>
          {assignments.length > 0 && (
            <button
              onClick={handleClearAll}
              className="px-4 py-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg flex items-center gap-2"
              title="Clear all workout assignments"
            >
              <AlertTriangle className="w-4 h-4" />
              Clear All
            </button>
          )}
          <button
            onClick={onSkip}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 flex items-center gap-2"
          >
            <SkipForward className="w-4 h-4" />
            Skip for now
          </button>
          <button
            onClick={handleSaveAssignments}
            disabled={saving || assignments.length === 0}
            className="coach-button-primary px-6 py-2 flex items-center gap-2 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Schedule'}
          </button>
        </div>
      </div>

      {/* Program Info */}
      <div className="bg-blue-50 border-blue-200 border rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-blue-900">
              Program Timeline
            </h3>
            <div className="text-sm text-blue-800 mt-1 space-y-1">
              <div>📅 <strong>Duration:</strong> {format(startDate, 'MMM d, yyyy')} - {format(endDate, 'MMM d, yyyy')}</div>
              <div>🏋️ <strong>Total Workouts:</strong> {generatedWorkouts.length}</div>
              <div>✅ <strong>Assigned:</strong> {assignments.length} / {generatedWorkouts.length}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Unassigned Workouts */}
        <div className="lg:col-span-1">
          <div className="coach-section">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Unassigned Workouts ({getUnassignedWorkouts().length})
            </h2>
            
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {getUnassignedWorkouts().map((workout) => (
                <div
                  key={workout.id}
                  draggable
                  onDragStart={() => setDraggedWorkout(workout)}
                  onDragEnd={() => setDraggedWorkout(null)}
                  onClick={() => setSelectedWorkout(workout)}
                  className="p-3 border border-gray-200 rounded-lg cursor-move hover:border-blue-300 hover:bg-blue-50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-2">
                      {getTypeIcon(workout.type)}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm text-gray-900 truncate">
                          {workout.name}
                        </div>
                        <div className="text-xs text-gray-500 space-x-2">
                          <span>{workout.estimatedDuration}min</span>
                          <span>•</span>
                          <span className={`px-1.5 py-0.5 rounded text-xs ${getDifficultyColor(workout.difficulty)}`}>
                            Level {workout.difficulty}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-2 text-xs text-gray-600">
                    {workout.exercises.length} exercises
                  </div>
                </div>
              ))}
              
              {getUnassignedWorkouts().length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle className="w-8 h-8 mx-auto mb-2" />
                  <p className="text-sm">All workouts assigned!</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Calendar */}
        <div className="lg:col-span-3">
          <div className="coach-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                Program Calendar
              </h2>
              
              {/* Month Navigation */}
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => navigateMonth('prev')}
                    disabled={!canNavigatePrev()}
                    className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Previous month"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <h3 className="text-lg font-semibold text-gray-900 min-w-[140px] text-center">
                    {format(currentMonth, 'MMMM yyyy')}
                  </h3>
                  <button
                    onClick={() => navigateMonth('next')}
                    disabled={!canNavigateNext()}
                    className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Next month"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
                
                <button
                  onClick={() => setCurrentMonth(startDate)}
                  className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded"
                  title="Go to program start month"
                >
                  Start
                </button>
              </div>
            </div>
            
            {/* Monthly Assignment Stats */}
            {(() => {
              const stats = getMonthlyAssignmentStats()
              return (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between text-sm">
                    <div className="text-blue-800">
                      <strong>This month:</strong> {stats.currentMonth} workouts assigned
                    </div>
                    {stats.otherMonths > 0 && (
                      <div className="text-blue-600">
                        +{stats.otherMonths} in other months
                      </div>
                    )}
                  </div>
                </div>
              )
            })()}
            
            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-px bg-gray-200 rounded-lg overflow-hidden">
              {/* Day Headers */}
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="bg-gray-50 p-3 text-center text-sm font-medium text-gray-700">
                  {day}
                </div>
              ))}
              
              {/* Calendar Days */}
              {calendarDays.map((day) => {
                const dayWorkouts = getWorkoutsForDate(day)
                const isInProgramRange = day >= startDate && day <= endDate
                const isInCurrentMonth = isSameMonth(day, currentMonth)
                const isToday = isSameDay(day, new Date())
                
                return (
                  <div
                    key={day.toISOString()}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault()
                      if (draggedWorkout && isInProgramRange) {
                        handleWorkoutDrop(draggedWorkout, day)
                      }
                    }}
                    className={`min-h-[120px] p-2 relative ${
                      !isInCurrentMonth 
                        ? 'bg-gray-50 text-gray-300' 
                        : isInProgramRange 
                          ? 'bg-white border-2 border-dashed border-gray-200 hover:border-blue-300' 
                          : 'bg-gray-50'
                    } ${isToday ? 'ring-2 ring-blue-500' : ''}`}
                  >
                    <div className={`text-sm font-medium mb-2 ${
                      !isInCurrentMonth 
                        ? 'text-gray-300'
                        : isInProgramRange 
                          ? 'text-gray-900' 
                          : 'text-gray-400'
                    }`}>
                      {format(day, 'd')}
                      {isToday && (
                        <span className="ml-1 text-xs bg-blue-500 text-white px-1 rounded">Today</span>
                      )}
                    </div>
                    
                    {dayWorkouts.map((workout) => (
                      <div
                        key={workout.id}
                        draggable
                        onDragStart={() => setDraggedWorkout(workout)}
                        onDragEnd={() => setDraggedWorkout(null)}
                        onClick={() => setSelectedWorkout(workout)}
                        className="bg-blue-100 border border-blue-200 rounded p-2 mb-1 cursor-move hover:bg-blue-200 transition-colors group"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1">
                            {getTypeIcon(workout.type)}
                            <span className="text-xs font-medium text-blue-900 truncate">
                              {workout.name.replace(/^Week \d+ - /, '')}
                            </span>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleWorkoutRemove(workout.id)
                            }}
                            className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 text-xs"
                          >
                            ×
                          </button>
                        </div>
                        <div className="text-xs text-blue-700 mt-1">
                          {workout.estimatedDuration}min
                        </div>
                      </div>
                    ))}
                    
                    {isInProgramRange && isInCurrentMonth && dayWorkouts.length === 0 && (
                      <div className="text-xs text-gray-400 text-center mt-4">
                        Drop workout here
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Workout Detail Modal */}
      {selectedWorkout && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Workout Details</h2>
                <button
                  onClick={() => setSelectedWorkout(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <div className="text-sm text-gray-500">Workout Name</div>
                  <div className="font-medium">{selectedWorkout.name}</div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-gray-500">Duration</div>
                    <div className="font-medium">{selectedWorkout.estimatedDuration} minutes</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Difficulty</div>
                    <div className={`inline-flex px-2 py-1 rounded text-sm font-medium ${getDifficultyColor(selectedWorkout.difficulty)}`}>
                      Level {selectedWorkout.difficulty}
                    </div>
                  </div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-500 mb-2">Exercises ({selectedWorkout.exercises.length})</div>
                  <div className="space-y-2">
                    {selectedWorkout.exercises.map((exercise, idx) => (
                      <div key={idx} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                        <div className="font-medium text-sm">{exercise.name}</div>
                        <div className="text-sm text-gray-600">
                          {exercise.sets && `${exercise.sets} sets`} {exercise.reps}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end gap-2 mt-6">
                <button
                  onClick={() => setSelectedWorkout(null)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
